import { MouseEventHandler, useRef, useState } from 'react';
import { useConditionalLayoutEffect } from 'react-delta';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { MenuItemConfig } from 'common/types';
import { createTestId } from 'common/element';

import { getMenuItem } from './DropdownItems';

type SubMenuTriggerProps = {
  item: MenuItemConfig;
  done: JSX.Element | null;
  closeBefore: (fn?: MouseEventHandler) => MouseEventHandler;
  itemKey: string;
};

function SubMenuTrigger({ item, done, closeBefore, itemKey }: SubMenuTriggerProps): JSX.Element {
  const [show, setShow] = useState(false);
  const [shouldPositionLeft, setShouldPositionLeft] = useState(false);
  const subMenuRef = useRef<HTMLDivElement>(null);
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);

  useConditionalLayoutEffect(() => {
    const { x = 0, width = 0 } = subMenuRef?.current?.getBoundingClientRect() ?? {};
    const documentWidth = document.documentElement.clientWidth;

    setShouldPositionLeft(x + width > documentWidth);
  }, show && !shouldPositionLeft && subMenuRef.current); // !shouldPositionLeft ensures we don't try to re-position it

  const handleMouseEnter = () => {
    setShow(true);
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };

  const handleMouseLeave = () => {
    const id = setTimeout(() => setShow(false), 50);
    setTimeoutId(id);
  };

  const additionalClasses = shouldPositionLeft ? 'position-left' : '';

  const overlay = (
    <div
      ref={subMenuRef}
      className={`Dropdown__Menu Dropdown__Menu__SubMenu ${additionalClasses}`}
      style={{ display: show ? 'block' : 'none' }}
    >
      {item.subMenuItems?.map((subMenuItem, index) =>
        getMenuItem(
          { ...subMenuItem, testId: createTestId(item.testId, subMenuItem.key) },
          closeBefore,
          index,
          `${itemKey}-submenu-${index}`,
          'div',
        ),
      )}
    </div>
  );

  return (
    <div className="DropdownItems__Item--hasSubMenu" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div>
        {done}
        {item.children || item.text}
      </div>
      <ChevronRightIcon className="DropdownItems__RightIcon" />
      {overlay}
    </div>
  );
}

export default SubMenuTrigger;
